import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import clsx from "clsx";
import PropTypes from "prop-types";
import {
  Tooltip,
  tooltipPlacement,
} from "@visiblethread/react-components/dist/components/Tooltip";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { ERROR_MESSAGES } from "hooks/dictionary/useValidatePopupExpression";
import { hasSpecialRegexCharacters } from "utils/dictionary";

import {
  getRegularExpressionGuidanceText,
  isExpressionValid,
} from "layout/dictionary/edit/helpers";
import {
  editItem,
  setRegexPanel,
} from "slices/settings/slices/acronymIgnoreSlice";
import { selectAcronymIgnore } from "slices/settings/selectors";
import SlidingPanel, {
  ANIMATION_DURATION,
  runHideAnimation,
} from "components/SlidingPanel";

const FOCUS_DELAY = 200;

const RegexPopup = ({ ignoreListItem }) => {
  const [expression, setExpression] = useState(ignoreListItem.term || "");
  const [errorConfirm, setErrorConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { ignoreList } = useSelector(selectAcronymIgnore);
  const dispatch = useDispatch();
  const exprRef = useRef(null);
  const popupRef = useRef();

  const closePopup = () => {
    runHideAnimation(popupRef);

    // Wait for animation to finish and then close popup
    setTimeout(() => {
      dispatch(
        setRegexPanel({
          showRegex: false,
          ignoreListItem: { term: "", regex: false },
        })
      );
    }, ANIMATION_DURATION);
  };

  const handleExpressionChange = ({ target: { value } }) => {
    setExpression(value);
  };

  const handleConfirmClick = () => {
    // If error is present display error layout for expression input and focus it
    if (errorMessage) {
      setErrorConfirm(true);
      exprRef.current.focus();
      return;
    }

    const index = ignoreList.findIndex(
      (item) => item.term === ignoreListItem.term
    );
    const editedItem = {
      ...ignoreListItem,
      term: expression,
      regex: true,
    };

    dispatch(editItem({ index, editedItem }));
    closePopup();
  };

  useEffect(() => {
    setTimeout(() => {
      exprRef.current?.focus();
    }, FOCUS_DELAY);
  }, []);

  const errorWidget = () => {
    if (!errorMessage) return null;
    // Don't display error for empty expression while editing (This error should be displayed only on "Confirm" button click)
    if (!errorMessage && errorMessage === ERROR_MESSAGES.EMPTY_EXPRESSION) {
      return null;
    }

    return (
      <span className="field_inline_message" data-testid="error-widget">
        <span className="field_inline_icon">
          <Tooltip
            content={errorMessage.DETAILED}
            placement={tooltipPlacement.bottomRight}
          />
        </span>
        {/* Display inline message only on "Confirm" button click */}
        {errorConfirm && (
          <span className="brief_message">{errorMessage.BRIEF}</span>
        )}
      </span>
    );
  };

  useEffect(() => {
    if (expression === "") {
      setErrorMessage(ERROR_MESSAGES.EMPTY_EXPRESSION);
      return;
    }

    if (
      !isExpressionValid(true, expression) ||
      !hasSpecialRegexCharacters(expression)
    ) {
      setErrorMessage(ERROR_MESSAGES.REGEXP_NOT_VALID);
      return;
    }

    setErrorMessage(null);
  }, [expression]);

  return (
    <SlidingPanel
      panelRef={popupRef}
      dataTestId="regex-popup"
      onDismiss={closePopup}
    >
      <Modal.Header closeButton>
        <h6 className="edit_dictionary_page__wildcard_popup__title">
          <b>
            Edit {ignoreListItem.term && `"${ignoreListItem.term}" `}Regular
            Expression
          </b>
        </h6>
      </Modal.Header>
      <div className="edit_dictionary_page__wildcard_popup__content_wrapper">
        <Modal.Body>
          <label
            htmlFor="regex-popup-name"
            className={clsx(
              "edit_dictionary_page__wildcard_popup__name_label edit_dictionary_page__wildcard_popup__field_wrapper",
              errorConfirm && "error_confirm"
            )}
          >
            Term
            <div className="edit_dictionary_page__wildcard_popup__term field">
              <input
                ref={exprRef}
                type="text"
                id="regex-popup-name"
                value={expression}
                placeholder="Enter regular expression"
                onChange={handleExpressionChange}
                className="field_input"
              />
              {errorWidget()}
            </div>
          </label>
        </Modal.Body>
        <div className="edit_dictionary_page__wildcard_popup__separator" />
        {getRegularExpressionGuidanceText()}
      </div>
      <Modal.Footer>
        <Button
          variant={buttonVariants.primary}
          onClick={handleConfirmClick}
          content="Confirm"
          dataTest="confirm-button"
        />
        <Button
          variant={buttonVariants.secondary}
          onClick={closePopup}
          content="Cancel"
          dataTest="cancel-button"
        />
      </Modal.Footer>
    </SlidingPanel>
  );
};

RegexPopup.propTypes = {
  ignoreListItem: PropTypes.shape({
    term: PropTypes.string,
    regex: PropTypes.bool,
  }).isRequired,
};

export default RegexPopup;
